import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../lib/api';
import { useAuth } from '../lib/auth/AuthProvider';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Plus, Users, Settings, Building2, CreditCard } from 'lucide-react';
import Navigation from '../components/Navigation';
import { useTranslation } from 'react-i18next';

const Dashboard = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const { t } = useTranslation();

  const [myGroups, setMyGroups] = useState([]);
  const [groupsLoading, setGroupsLoading] = useState(true);

  const [myOrders, setMyOrders] = useState([]);
  const [ordersLoading, setOrdersLoading] = useState(true);

  // UI busy state (prevents double clicks on resume/cancel)
  const [busyOrderId, setBusyOrderId] = useState(null);

  const displayName =
    (user?.displayName && user.displayName.split(' ')[0]) ||
    (user?.email && user.email.split('@')[0]) ||
    t('dashboard.fallbackName', { defaultValue: 'there' });

  const fetchMyOrders = async () => {
    try {
      const res = await api.get('/orders/my');
      setMyOrders(Array.isArray(res.data) ? res.data : []);
    } catch (error) {
      console.error('Failed to load orders', error.response || error);
      toast.error(
        t('dashboard.errors.loadOrders', {
          defaultValue: 'Failed to load your orders.',
        })
      );
    } finally {
      setOrdersLoading(false);
    }
  };

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get('session_id');
    const canceled = params.get('canceled');

    if (authLoading) return;

    if (!user) {
      setGroupsLoading(false);
      setOrdersLoading(false);
      return;
    }

    if (sessionId) {
      toast.success(
        t('dashboard.payment.success', {
          defaultValue:
            'Payment successful! Your membership or order has been updated.',
        })
      );
      window.history.replaceState({}, '', '/dashboard');
    }

    if (canceled) {
      toast.error(
        t('dashboard.payment.canceled', {
          defaultValue: 'Checkout was canceled.',
        })
      );
      window.history.replaceState({}, '', '/dashboard');
    }

    const fetchMyGroups = async () => {
      try {
        const res = await api.get('/dashboard/my-groups');
        setMyGroups(res.data);
      } catch (error) {
        console.error('Failed to load groups', error.response || error);
        toast.error(
          error?.response?.status === 401
            ? t('dashboard.errors.sessionExpired', {
                defaultValue: 'Your session has expired. Please sign in again.',
              })
            : t('dashboard.errors.loadGroups', {
                defaultValue: 'Failed to load your groups.',
              })
        );
      } finally {
        setGroupsLoading(false);
      }
    };

    fetchMyGroups();
    fetchMyOrders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authLoading, user, t]);

  const getRoleBadge = (role) => {
    const variants = { owner: 'default', admin: 'secondary', member: 'outline' };
    return (
      <Badge variant={variants[role] || 'outline'}>{role?.toUpperCase()}</Badge>
    );
  };

  const getMembershipBadge = (status) => {
    if (!status) {
      return (
        <Badge variant="outline">
          {t('dashboard.membershipStatus.none', {
            defaultValue: 'No membership',
          })}
        </Badge>
      );
    }

    if (status === 'active') {
      return (
        <Badge variant="default">
          {t('dashboard.membershipStatus.active', {
            defaultValue: 'Active membership',
          })}
        </Badge>
      );
    }

    return (
      <Badge variant="destructive">
        {t('dashboard.membershipStatus.expired', {
          defaultValue: 'Membership expired',
        })}
      </Badge>
    );
  };

  const fmtAmount = (n, cur = 'USD') =>
    new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: cur || 'USD',
    }).format(Number(n || 0));

  const normalizeStatus = (s) => String(s || '').toLowerCase().trim();

  const statusBadge = (s) => {
    const status = normalizeStatus(s);

    const map = {
      paid: 'default',
      completed: 'default',
      pending: 'secondary',
      failed: 'destructive',
      refunded: 'outline',
      canceled: 'outline',
      cancelled: 'outline',
    };

    const labelKey = `dashboard.orders.status.${status || 'unknown'}`;
    const defaultLabels = {
      paid: 'Paid',
      completed: 'Completed',
      pending: 'Pending',
      failed: 'Failed',
      refunded: 'Refunded',
      canceled: 'Canceled',
      cancelled: 'Canceled',
      unknown: t('dashboard.orders.status.unknown', {
        defaultValue: 'Unknown',
      }),
    };

    return (
      <Badge variant={map[status] || 'outline'}>
        {t(labelKey, {
          defaultValue: defaultLabels[status] || defaultLabels.unknown,
        })}
      </Badge>
    );
  };

  const orderTypeLabel = (itemType) => {
    const type = String(itemType || '').toLowerCase().trim();

    const map = {
      event: ['dashboard.orders.types.event', 'Event'],
      product: ['dashboard.orders.types.product', 'Product'],
      cart: ['dashboard.orders.types.cart', 'Cart'],
      membership: ['dashboard.orders.types.membership', 'Membership'],
      group_create: ['dashboard.orders.types.groupCreate', 'Group creation'],
    };

    const [key, fallback] = map[type] || [
      'dashboard.orders.types.unknown',
      'Unknown',
    ];

    return t(key, { defaultValue: fallback });
  };

  const resumePendingOrder = async (orderId) => {
    try {
      setBusyOrderId(orderId);

      // Backend should return: { url: <stripe checkout session url> }
      const res = await api.post(`/orders/${orderId}/resume`);
      const url = res?.data?.url;

      if (!url) {
        toast.error(
          t('dashboard.orders.resumeError', {
            defaultValue: 'Unable to resume checkout for this order.',
          })
        );
        return;
      }

      window.location.href = url;
    } catch (err) {
      console.error('resumePendingOrder error', err?.response || err);
      toast.error(
        t('dashboard.orders.resumeError', {
          defaultValue: 'Unable to resume checkout for this order.',
        })
      );
    } finally {
      setBusyOrderId(null);
    }
  };

  const cancelPendingOrder = async (orderId) => {
    const ok = window.confirm(
      t('dashboard.orders.cancelConfirm', {
        defaultValue:
          'Are you sure you want to cancel this order? This action can’t be undone.',
      })
    );
    if (!ok) return;

    try {
      setBusyOrderId(orderId);

      await api.post(`/orders/${orderId}/cancel`);

      toast.success(
        t('dashboard.orders.cancelSuccess', {
          defaultValue: 'Order canceled.',
        })
      );

      // Update locally so it disappears immediately (or shows as canceled)
      setMyOrders((prev) =>
        prev.map((o) =>
          o.id === orderId ? { ...o, status: 'canceled' } : o
        )
      );

      // Optional: refetch to stay fully consistent with DB
      // await fetchMyOrders();
    } catch (err) {
      console.error('cancelPendingOrder error', err?.response || err);
      toast.error(
        t('dashboard.orders.cancelError', {
          defaultValue: 'Unable to cancel this order.',
        })
      );
    } finally {
      setBusyOrderId(null);
    }
  };

  const showGroupsLoading = authLoading || groupsLoading;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      <Navigation />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1
            className="text-4xl font-bold text-slate-900 mb-2"
            data-testid="dashboard-title"
          >
            {t('dashboard.title', {
              name: displayName,
              defaultValue: `Welcome, ${displayName}`,
            })}
          </h1>
          <p className="text-slate-600">
            {t('dashboard.subtitle', {
              defaultValue:
                "Here’s an overview of your communities, memberships, and orders.",
            })}
          </p>
        </div>

        {/* Quick Actions */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <Button
            onClick={() => navigate('/groups/create')}
            className="h-24 flex flex-col items-center justify-center space-y-2"
            data-testid="create-group-btn"
          >
            <Plus size={24} />
            <span>
              {t('dashboard.actions.createGroup', {
                defaultValue: 'Create a new group',
              })}
            </span>
          </Button>

          <Button
            onClick={() => navigate('/groups')}
            variant="outline"
            className="h-24 flex flex-col items-center justify-center space-y-2"
            data-testid="browse-groups-btn"
          >
            <Users size={24} />
            <span>
              {t('dashboard.actions.browseGroups', {
                defaultValue: 'Browse groups',
              })}
            </span>
          </Button>
        </div>

        {/* My Groups */}
        <div className="mb-10">
          <h2 className="text-2xl font-bold text-slate-900 mb-4">
            {t('dashboard.myGroups.title', {
              defaultValue: 'My groups',
            })}
          </h2>

          {showGroupsLoading ? (
            <Card>
              <CardContent className="py-12 text-center text-slate-500">
                {t('dashboard.myGroups.loading', {
                  defaultValue: 'Loading your groups...',
                })}
              </CardContent>
            </Card>
          ) : myGroups.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <Building2 className="mx-auto mb-4 text-slate-400" size={48} />
                <h3 className="text-lg font-semibold mb-2">
                  {t('dashboard.myGroups.emptyTitle', {
                    defaultValue: 'You’re not in any groups yet',
                  })}
                </h3>
                <p className="text-slate-600 mb-4">
                  {t('dashboard.myGroups.emptyDescription', {
                    defaultValue:
                      'Create a new group for your community or join an existing one.',
                  })}
                </p>
                <Button
                  onClick={() => navigate('/groups/create')}
                  data-testid="create-first-group-btn"
                >
                  <Plus size={16} className="mr-2" />
                  {t('dashboard.myGroups.createFirst', {
                    defaultValue: 'Create your first group',
                  })}
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {myGroups.map(({ group, role, membership_status }) => {
                const requiresSubscription = !!group.require_subscription;
                const hasActiveMembership = membership_status === 'active';

                return (
                  <Card
                    key={group.id}
                    className="hover:shadow-lg transition-shadow cursor-pointer"
                    onClick={() => navigate(`/groups/${group.id}/space`)}
                    data-testid={`group-card-${group.id}`}
                  >
                    <CardHeader>
                      <div className="flex items-start space-x-4">
                        <img
                          src={
                            group.logo ||
                            `https://ui-avatars.com/api/?name=${encodeURIComponent(
                              group.name
                            )}&size=80`
                          }
                          alt={group.name}
                          className="w-16 h-16 rounded-lg object-cover"
                        />
                        <div className="flex-1 min-w-0">
                          <CardTitle className="text-lg truncate">
                            {group.name}
                          </CardTitle>
                          <div className="flex flex-wrap gap-2 mt-2">
                            {getRoleBadge(role)}
                            {getMembershipBadge(membership_status)}
                            {requiresSubscription && (
                              <Badge variant="outline">
                                {t('dashboard.myGroups.subscriptionRequired', {
                                  defaultValue: 'Subscription required',
                                })}
                              </Badge>
                            )}
                          </div>

                          {requiresSubscription && !hasActiveMembership && (
                            <p className="mt-1 text-xs text-amber-700">
                              {t('groupSpace.subscription.requiredLabel', {
                                defaultValue:
                                  'Subscription required to access this group',
                              })}
                            </p>
                          )}
                        </div>
                      </div>
                    </CardHeader>

                    <CardContent>
                      <p className="text-sm text-slate-600 line-clamp-2 mb-4">
                        {group.description}
                      </p>

                      <div className="flex flex-wrap gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/groups/${group.id}/space`);
                          }}
                          className="flex-1 min-w-[120px]"
                          data-testid={`view-group-btn-${group.id}`}
                        >
                          <Users size={14} className="mr-1" />
                          {t('dashboard.myGroups.view', {
                            defaultValue: 'Go to group space',
                          })}
                        </Button>

                        {requiresSubscription && !hasActiveMembership && (
                          <Button
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/groups/${group.id}/space`);
                            }}
                            className="flex-1 min-w-[120px]"
                            data-testid={`subscribe-group-btn-${group.id}`}
                          >
                            <CreditCard size={14} className="mr-1" />
                            {t('dashboard.myGroups.subscribe', {
                              defaultValue: 'Subscribe',
                            })}
                          </Button>
                        )}

                        {(role === 'owner' || role === 'admin') && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/groups/${group.id}/admin`);
                            }}
                            className="flex-1 min-w-[120px]"
                            data-testid={`admin-group-btn-${group.id}`}
                          >
                            <Settings size={14} className="mr-1" />
                            {t('dashboard.myGroups.admin', {
                              defaultValue: 'Admin area',
                            })}
                          </Button>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </div>

        {/* My Orders */}
        <div>
          <h2 className="text-2xl font-bold text-slate-900 mb-4">
            {t('dashboard.orders.title', {
              defaultValue: 'My orders',
            })}
          </h2>

          {authLoading || ordersLoading ? (
            <Card>
              <CardContent className="py-12 text-center text-slate-500">
                {t('dashboard.orders.loading', {
                  defaultValue: 'Loading your orders...',
                })}
              </CardContent>
            </Card>
          ) : myOrders.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center text-slate-500">
                {t('dashboard.orders.empty', {
                  defaultValue: 'You don’t have any orders yet.',
                })}
              </CardContent>
            </Card>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {myOrders.map((o) => {
                const isPending = normalizeStatus(o.status) === 'pending';
                const isBusy = busyOrderId === o.id;

                return (
                  <Card key={o.id}>
                    <CardHeader>
                      <CardTitle className="flex items-center justify-between">
                        <span>
                          {t('dashboard.orders.orderLabel', {
                            id: String(o.id || '').slice(0, 8),
                            defaultValue: `Order #${String(o.id || '').slice(
                              0,
                              8
                            )}`,
                          })}
                        </span>
                        {statusBadge(o.status)}
                      </CardTitle>
                      <CardDescription>
                        {(o.created_at &&
                          new Date(o.created_at).toLocaleString()) ||
                          '—'}
                      </CardDescription>
                    </CardHeader>

                    <CardContent className="text-sm text-slate-700 space-y-2">
                      <div>
                        <strong>
                          {t('dashboard.orders.total', {
                            defaultValue: 'Total',
                          })}
                          :
                        </strong>{' '}
                        {fmtAmount(o.total_amount, o.currency)}
                      </div>

                      <div>
                        <strong>
                          {t('dashboard.orders.typeLabel', {
                            defaultValue: 'Type',
                          })}
                          :
                        </strong>{' '}
                        {orderTypeLabel(o.item_type)}
                      </div>

                      {/* Existing navigation buttons */}
                      {o.item_type === 'event' && o.group_id && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => navigate(`/groups/${o.group_id}/space`)}
                        >
                          {t('dashboard.orders.viewEventGroup', {
                            defaultValue: 'View event group',
                          })}
                        </Button>
                      )}

                      {o.item_type === 'product' && o.group_id && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => navigate(`/groups/${o.group_id}/space`)}
                        >
                          {t('dashboard.orders.viewStore', {
                            defaultValue: 'View store',
                          })}
                        </Button>
                      )}

                      {/* ✅ NEW: Pending order actions */}
                      {isPending && (
                        <div className="flex flex-wrap gap-2 pt-2">
                          <Button
                            size="sm"
                            onClick={() => resumePendingOrder(o.id)}
                            disabled={isBusy}
                            className="flex-1 min-w-[140px]"
                            data-testid={`resume-order-${o.id}`}
                          >
                            {t('dashboard.orders.resume', {
                              defaultValue: 'Continue checkout',
                            })}
                          </Button>

                          <Button
                            size="sm"
                            variant="destructive"
                            onClick={() => cancelPendingOrder(o.id)}
                            disabled={isBusy}
                            className="flex-1 min-w-[140px]"
                            data-testid={`cancel-order-${o.id}`}
                          >
                            {t('dashboard.orders.cancel', {
                              defaultValue: 'Cancel order',
                            })}
                          </Button>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
